import {
  ExtendedTypography,
  createSyntheticMouseEvent,
} from '@issuemanager/apps/client/components';
import {
  Button,
  Stack,
  useEventCallback,
  type ButtonProps,
} from '@mui/material';
import React from 'react';

export interface ExtendedAutocompleteActionsProps {
  ClearButtonProps?: ButtonProps;
  ApplyButtonProps?: ButtonProps;
  CancelButtonProps?: ButtonProps;
}

export function ExtendedAutocompleteActions(
  props: ExtendedAutocompleteActionsProps,
): React.ReactElement {
  const {
    ClearButtonProps = {},
    ApplyButtonProps = {},
    CancelButtonProps = {},
  } = props;
  const { children: clearButtonChildren, ...clearButtonProps } =
    ClearButtonProps;
  const { children: applyButtonChildren, ...applyButtonProps } =
    ApplyButtonProps;
  const { children: cancelButtonChildren, ...cancelButtonProps } =
    CancelButtonProps;

  const handleApplyKeyDown = useEventCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        applyButtonProps.onClick?.(createSyntheticMouseEvent(event));
      }
    },
  );

  const handleCancelKeyDown = useEventCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        cancelButtonProps.onClick?.(createSyntheticMouseEvent(event));
      }
    },
  );

  const handleClearKeyDown = useEventCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        clearButtonProps.onClick?.(createSyntheticMouseEvent(event));
      }
    },
  );

  return (
    <Stack
      direction='row'
      justifyContent='space-between'
      padding={1}
      paddingTop={2}
    >
      <Button
        variant='outlined'
        data-testid='clear-button'
        {...clearButtonProps}
        onKeyDown={handleClearKeyDown}
      >
        <ExtendedTypography variant='emphasis' whiteSpace='nowrap'>
          {clearButtonChildren}
        </ExtendedTypography>
      </Button>
      <Stack direction='row' justifyContent='flex-end' gap={2}>
        <Button
          variant='text'
          data-testid='cancel-button'
          {...cancelButtonProps}
          onKeyDown={handleCancelKeyDown}
        >
          <ExtendedTypography variant='emphasis' whiteSpace='nowrap'>
            {cancelButtonChildren}
          </ExtendedTypography>
        </Button>
        {applyButtonProps.onClick ? (
          <Button
            variant='contained'
            data-testid='apply-button'
            {...applyButtonProps}
            onKeyDown={handleApplyKeyDown}
          >
            <ExtendedTypography variant='emphasis' whiteSpace='nowrap'>
              {applyButtonChildren}
            </ExtendedTypography>
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
}
